<template>
  <v-timeline-item
    :fill-dot="true"
    dot-color="surface-brand-bold-hover"
    size="small"
    width="100%"
    class="qa-item-thread-answer"
  >
    <template #icon>
      <v-avatar color="surface-brand-bold-hover" size="26">
        {{ answer.userShortInitials }}
      </v-avatar>
    </template>

    <div class="qa-item-thread-answer__content">
      <div class="qa-item-thread-answer__header">
        <span class="qa-item-thread-answer__username">
          {{ answer.userName }}
        </span>

        <span class="text-caption qa-item-thread-answer__creation-date">
          {{ $t('project.question-answer.QuestionAnswerItemThreadAnswer.publishedOn') }}
          <DateDisplay :value="answer.createdAt" with-hours />
        </span>
      </div>

      <div class="qa-item-thread-answer__description mt-3 text-text-secondary">
        <MentionableContent
          :html="answer.description"
          :mentionables="mentionables"
        />
      </div>

      <div
        v-if="answerFiles.length"
        class="qa-item-thread-answer__attachments mt-3"
        data-locator="qa-answer-attachments"
      >
        <div
          class="qa-item-thread-answer__attachments-list"
          data-locator="qa-answer-attachments-list"
        >
          <DocumentsPickerSelectedItem
            v-for="file in answerFiles"
            :key="`file-${file.id}`"
            :file="file"
            :can-remove="false"
            layout="inline"
            locator-prefix="qa-answer-attachments"
          />
        </div>
      </div>
    </div>
  </v-timeline-item>
</template>

<script>
import DateDisplay from '@/common/DateDisplay.vue'
import DocumentsPickerSelectedItem from '@/common/documents-picker/DocumentsPickerSelectedItem.vue'
import MentionableContent from '@/common/mentions/MentionableContent.vue'

export default {
  name: 'QuestionAnswerItemThreadAnswer',
  components: {
    DateDisplay,
    DocumentsPickerSelectedItem,
    MentionableContent,
  },
  props: {
    answer: {
      type: Object,
      required: true,
    },
    mentionables: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    answerFiles () {
      const files = Array.isArray(this.answer?.files) ? this.answer.files : []
      return files
        .filter(Boolean)
        .map(f => ({
          ...f,
          type: 'file',
          name: f.name ?? f.basename,
          basename: f.basename ?? f.name,
        }))
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

:deep(.v-timeline-item__body) {
  min-width: 0;
}

:deep(.v-timeline-item__dot) {
  width: 26px;
  height: 26px;
  box-shadow: none;
  font-size: 12px;
  // Fixes an alignment problem with the username
  margin-top: 4px;
}

:deep(.v-timeline-item__divider) {
  // The margin is handled this way for timeline dots
  min-width: 44px;
}

.qa-item-thread-answer {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 7px;
  }

  &__username {
    font-size: 16px;
    font-weight: 500;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
    color: rgb(var(--v-theme-text-tertiary));
  }

  &__creation-date {
    color: rgb(var(--v-theme-text-tertiary));
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__description {
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  &__attachments {
    min-width: 0;
  }

  &__attachments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }
  }
}
</style>
